<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    icon="fa-code-branch"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="stageForm" :labelColumnSize="3">
      <FormRow label="Name">
        <FormInputText v-model="stageData.name" field="name" />
      </FormRow>
      <FormRow label="Category">
        <FormSelect v-model="stageData.category" field="category" :options="stageCategoryOptions" />
      </FormRow>
      <FormRow label="Lead Time">
        <FormInputText type="number" v-model="stageData.leadTime" field="leadTime" addon="mins" note="Lead time is used to estimate panel completion dates for orders" />
      </FormRow>
      <FormRow label="Highlight Colour" helpTip="Highlight colour for the stage includes production tag, station theme, etc">
        <FormColourTag v-model="stageData.highlightColour" field="highlightColour" />
      </FormRow>

      <FormHorizontalRule />
      <ContentTitle>Settings</ContentTitle>

      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="Show Stage Tag" helpTip="If selected, the stage will show up as a tag (with highlight colour) in the production panel list">
            <FormCheckbox v-model="stageData.isStageTagShown" field="isStageTagShown" />
          </FormRow>
        </div>
        <div class="col-6">
          <FormRow label="Requires Scan In" helpTip="If selected, panels must be scanned in by a station at this stage before completion by this stage or fast-forwarded by other stages">
            <FormCheckbox v-model="stageData.requiresScanInAtStage" field="requiresScanInAtStage" />
          </FormRow>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="External Stage" helpTip="Flags if this stage is processed externally. All services associated with this stage will default to external in order entry">
            <FormCheckbox v-model="stageData.isExternal" field="isExternal" />
          </FormRow>
        </div>
        <div class="col-6">
          <FormRow label="Requires Scan Out" helpTip="If set, panels must be scanned out by a station at this stage before fast-forwarded by other stages">
            <FormCheckbox v-model="stageData.requiresScanOutAtStage" field="requiresScanOutAtStage" />
          </FormRow>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="Floating Stage" helpTip="Flags if this stage is floating. Floating stages can be completed / fast-forwarded at any point during production. But they must be completed eventually">
            <FormCheckbox v-model="stageData.isFloatingStage" field="isFloatingStage" />
          </FormRow>
        </div>
        <div class="col-6">
          
        </div>
      </div>

      <FormHorizontalRule />
      <ContentTitle>Print Settings</ContentTitle>

      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="Print Label On Scan In" helpTip="If selected, panels will have their label printed out when scanned in at this stage">
            <FormCheckbox v-model="stageData.printLabelOnStageScanIn" field="printLabelOnStageScanIn" />
          </FormRow>
        </div>
        <div class="col-6">
          <FormRow label="Requires Label Printer" helpTip="If selected, the stage will require a specific printer setup for all panel labels associated with this stage">
            <FormCheckbox v-model="stageData.requiresStageLabelPrinter" field="requiresStageLabelPrinter" />
          </FormRow>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="Print Label On Scan Out" helpTip="If selected, panels will have their label printed out when scanned out at this stage">
            <FormCheckbox v-model="stageData.printLabelOnStageScanOut" field="printLabelOnStageScanOut" />
          </FormRow>
        </div>
        <div class="col-6">
          <FormRow label="Requires Post Label Printer" helpTip="If selected, the stage will require a printer setup for panel labels when they complete this stage.">
            <FormCheckbox v-model="stageData.requiresPostStageLabelPrinter" field="requiresPostStageLabelPrinter" />
          </FormRow>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col-6">
          <FormRow label="Requires Order Printer" helpTip="If selected, orders that contain panels linked to this stage will require a specific printer setup for order sheets">
            <FormCheckbox v-model="stageData.requiresStageOrderPrinter" field="requiresStageOrderPrinter" />
          </FormRow>
        </div>
      </div>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import { ProductionStageCategoryTypes, ProductionStageCategoryTypeLookup } from '@/store/types'

export default {
  name: 'ProductionStageDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog
  },
  props: {
    productionStageId: Number,
    isCreateMode: Boolean
  },
  data() {
    return {
      stageData: {
      },
      stageForm: {},
      submitting: false
    }
  },
  created() {
    this.ProductionStageCategoryTypes = ProductionStageCategoryTypes
  },
  mounted() {
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Production Stage' : 'Edit Production Stage'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    stageCategoryOptions() {
      var optionList = _.clone(ProductionStageCategoryTypeLookup)

      // Filter out types if the account does not have access to it
      if(!this.accountHasProductFeature(this.AccountProductFeatureTypes.productionFurnace))
        optionList = optionList.filter(stageType => stageType.value != ProductionStageCategoryTypes.furnace)
      if(!this.accountHasProductFeature(this.AccountProductFeatureTypes.orderEditor))
        optionList = optionList.filter(stageType => stageType.value != ProductionStageCategoryTypes.waterjet)

      return optionList
    }
  },
  methods: {
    show(productionStage) {
      // Reset form
      this.stageData = {
        name: '',
        category: '',
        leadTime: 0,
        highlightColour: null,
        isStageTagShown: false,
        isExternal: false,
        isFloatingStage: false,
        requiresScanInAtStage: false,
        requiresScanOutAtStage: false,
        requiresStageLabelPrinter: false,
        requiresPostStageLabelPrinter: false,
        requiresStageOrderPrinter: false,
        printLabelOnStageScanIn: false,
        printLabelOnStageScanOut: false
      }
      this.stageForm = {}
      this.submitting = false

      if(productionStage != null) {
        this.stageData = _.cloneDeep(productionStage)
      }

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var stageData = _.cloneDeep(this.$data.stageData)

      if(this.isCreateMode) {
        this.$apiService.productionService.createProductionStage(stageData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.stageForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.productionService.updateProductionStage(stageData.id, stageData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.stageForm = this.processFormResponse(err)
        })
      }
    }
  }
}
</script>